// src/components/ClubsSection.js
// Section "Clubs" de la Communauté : explication, mes clubs, créer, rejoindre/parcourir.
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchWithAuth } from "../utils/api";
import "./ClubsSection.css";

// Couleur d'avatar dérivée du nom (stable)
const CLUB_COLORS = ["#0077b6", "#0096c7", "#2e9e5b", "#e0a400", "#8a6d3b", "#6f4a9e", "#c0507a"];
const clubColor = (name) => {
    let h = 0;
    for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return CLUB_COLORS[h % CLUB_COLORS.length];
};
const ClubAvatar = ({ club, size = 40 }) => (
    club.logo_url ? (
        <img src={club.logo_url} alt="" style={{ width: size, height: size, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
    ) : (
        <span style={{ width: size, height: size, borderRadius: 10, flexShrink: 0, background: clubColor(club.name), color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: size * 0.42, fontFamily: "var(--font-display)" }}>
            {(club.name || "?").charAt(0).toUpperCase()}
        </span>
    )
);

export default function ClubsSection({ apiUrl }) {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [myClubs, setMyClubs] = useState([]);
    const [allClubs, setAllClubs] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [creating, setCreating] = useState(false);
    const [search, setSearch] = useState("");

    const loadMyClubs = useCallback(() => {
        fetchWithAuth(`${apiUrl}/my-clubs`).then((r) => (r.ok ? r.json() : [])).then((d) => setMyClubs(Array.isArray(d) ? d : [])).catch(() => {});
    }, [apiUrl]);
    const loadAllClubs = useCallback(() => {
        fetchWithAuth(`${apiUrl}/clubs`).then((r) => (r.ok ? r.json() : [])).then((d) => setAllClubs(Array.isArray(d) ? d : [])).catch(() => {});
    }, [apiUrl]);
    useEffect(() => { loadMyClubs(); loadAllClubs(); }, [loadMyClubs, loadAllClubs]);

    const handleCreate = async () => {
        if (!name.trim()) return;
        setCreating(true);
        try {
            const res = await fetchWithAuth(`${apiUrl}/clubs`, { method: "POST", body: JSON.stringify({ name: name.trim(), description: desc.trim() || null }) });
            const data = await res.json();
            if (res.ok && data.id) { setShowCreate(false); setName(""); setDesc(""); navigate(`/clubs/${data.id}`); }
        } catch (e) { console.error("create club", e); } finally { setCreating(false); }
    };

    const handleJoin = async (clubId) => {
        try { await fetchWithAuth(`${apiUrl}/clubs/${clubId}/join`, { method: "POST" }); loadAllClubs(); loadMyClubs(); } catch (e) { console.error(e); }
    };

    return (
        <div className="clubs-section">
            <h2 className="community-section-title" style={{ marginBottom: 4 }}>{t("clubs.title")}</h2>
            <p className="community-subhint">{t("clubs.explainer")}</p>
            <p className="community-subhint">
                {t("clubs.fsssIntro")}{" "}
                <a href="https://susv.ch/fr/la-fede/membres-partenaires/clubs/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary-blue)", textDecoration: "underline", fontWeight: 600 }}>
                    {t("clubs.fsssLink")}
                </a>. {t("clubs.fsssJoinNote")}
            </p>

            <div className="clubs-actions">
                <button className="clubs-btn-primary" onClick={() => setShowCreate(true)}>{t("clubs.create")}</button>
            </div>

            {/* Mes clubs */}
            {myClubs.length > 0 && (
                <>
                    <h3 className="clubs-subtitle">{t("clubs.myClubs")}</h3>
                    <div className="clubs-my">
                        {myClubs.map((c) => (
                            <button key={c.id} className="clubs-card" onClick={() => navigate(`/clubs/${c.id}`)}>
                                <ClubAvatar club={c} />
                                <span className="clubs-card-name">{c.name}</span>
                                {c.role === "admin" && <span className="clubs-admin-badge">{t("clubs.admin")}</span>}
                            </button>
                        ))}
                    </div>
                </>
            )}

            {/* Découvrir les clubs (inline : recherche + liste) */}
            <h3 className="clubs-subtitle">{t("clubs.discover")}</h3>
            <input className="clubs-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("clubs.searchClub")} style={{ marginBottom: 10 }} />
            <ul className="clubs-browse-list clubs-browse-list--inline">
                {(() => {
                    const term = search.trim().toLowerCase();
                    const full = allClubs.filter((c) => !term || (c.name || "").toLowerCase().includes(term));
                    if (full.length === 0) return <li className="clubs-browse-empty">{t("clubs.noClubs")}</li>;
                    const list = full.slice(0, 40);
                    const rows = list.map((c) => (
                        <li key={c.id} className="clubs-browse-item">
                            <button className="clubs-browse-info" onClick={() => navigate(`/clubs/${c.id}`)}>
                                <ClubAvatar club={c} size={34} />
                                <span style={{ minWidth: 0 }}>
                                    <span className="clubs-card-name">{c.name}</span>
                                    <span className="clubs-browse-count">{t("clubs.memberCount", { count: c.memberCount })}</span>
                                </span>
                            </button>
                            {c.myStatus === "approved" ? (
                                <span className="clubs-status clubs-status--member">{t("clubs.memberBadge")}</span>
                            ) : c.myStatus === "pending" ? (
                                <span className="clubs-status clubs-status--pending">{t("clubs.pendingBadge")}</span>
                            ) : (
                                <button className="clubs-btn-primary clubs-btn-sm" onClick={() => handleJoin(c.id)}>{t("clubs.join")}</button>
                            )}
                        </li>
                    ));
                    if (full.length > list.length) {
                        rows.push(<li key="more" className="clubs-browse-empty">{t("clubs.refineSearch", { count: full.length - list.length })}</li>);
                    }
                    return rows;
                })()}
            </ul>

            {/* Modale création */}
            {showCreate && (
                <div className="clubs-overlay" onClick={() => setShowCreate(false)}>
                    <div className="clubs-modal" onClick={(e) => e.stopPropagation()}>
                        <h2 className="clubs-modal-title">{t("clubs.createTitle")}</h2>
                        <label className="clubs-label">{t("clubs.name")}</label>
                        <input autoFocus className="clubs-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("clubs.namePlaceholder")} />
                        <label className="clubs-label">{t("clubs.description")}</label>
                        <textarea className="clubs-input" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t("clubs.descriptionPlaceholder")} style={{ resize: "vertical" }} />
                        <div className="clubs-modal-actions">
                            <button className="clubs-btn-text" onClick={() => setShowCreate(false)}>{t("common.cancel")}</button>
                            <button className="clubs-btn-primary" disabled={creating || !name.trim()} onClick={handleCreate}>{creating ? t("clubs.creating") : t("clubs.createBtn")}</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
